import React, { Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { KpiFiltersButton, KpiFiltersPopover, KpiList, Notification } from '../kpis/remote-modules';
import { usePortalContainer } from './usePortalContainer';
import { KPI_LOCATION_VALUES, getSharedData, openViewKpiModal, useOpenKpiUpdatesDialog } from '../kpis';

const TEAM_VIEW_KPIS_TAB_SELECTOR = '#team-view-kpis-tab[aria-hidden="false"] > #team-view-kpis-tab-content';
type KpiFilters = { status?: number, includeArchived?: boolean };
export function KpiTabContent({ teamId }: { teamId: string }) {
  const { t } = useTranslation();
  const openKpiUpdatesDialog = useOpenKpiUpdatesDialog();

  const targetElement = usePortalContainer(TEAM_VIEW_KPIS_TAB_SELECTOR);

  const [showNotification, setShowNotification] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [kpiFilters, setKpiFilters] = useState<KpiFilters | undefined>({});
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  const sharedData = useMemo(() => getSharedData(KPI_LOCATION_VALUES.TEAM_PAGE_TAB), []);

  const handleKpiClick = useCallback(({ id }: { id: string }) => {
    openViewKpiModal(id, KPI_LOCATION_VALUES.TEAM_PAGE_ROW);
  }, []);

  const handleSubmitUpdates = useCallback(() => {
    setShowNotification(true);
  }, []);

  const handleUpdate = useCallback(
    ({ id }: { id: string }) => {
      openKpiUpdatesDialog({
        id: Number(id),
        location: KPI_LOCATION_VALUES.VIEW_KPI_UPDATE,
        onSubmit: handleSubmitUpdates,
      });
    },
    [handleSubmitUpdates, openKpiUpdatesDialog],
  );

  const handleFilterClick = useCallback(() => {
    setShowFilters((prev) => !prev);
  }, []);

  const handleFilterClose = useCallback(() => {
    setShowFilters(false);
  }, []);

  const handleFiltersChange = useCallback(
    (newFilters: KpiFilters) => {
      setKpiFilters(newFilters);
      setShowFilters(false);
    },
    [setShowFilters, setKpiFilters],
  );

  const mergedFilters = useMemo(
    () => ({
      teamResponsibleIds: Number(teamId),
      ...kpiFilters,
    }),
    [teamId, kpiFilters],
  );

  if (!targetElement) {
    return null;
  }

  return createPortal(
    <>
      <Suspense fallback={<div>Loading...</div>}>
        <div style={{ padding: '24px' }}>
          <div style={{ padding: '12px 4px' }}>
            <KpiFiltersButton isOpen={showFilters} onClick={handleFilterClick} buttonRef={filterButtonRef} />
          </div>

          <KpiFiltersPopover
            anchorEl={filterButtonRef.current}
            open={showFilters}
            onClose={handleFilterClose}
            filters={kpiFilters}
            onFiltersChange={handleFiltersChange}
          />

          <KpiList
            {...sharedData}
            filters={mergedFilters}
            groupBy="none"
            pageSize={50}
            showTableHeader
            enableFilters={false}
            onKpiClick={handleKpiClick}
            onUpdate={handleUpdate}
          />
        </div>
      </Suspense>
      {showNotification && (
        <Suspense fallback={null}>
          <Notification
            open={showNotification}
            message={t('KPI data has been updated successfully')}
            type="success"
            onClose={() => setShowNotification(false)}
          />
        </Suspense>
      )}
    </>,
    targetElement,
  );
}
